import React, { useCallback, useEffect, useState } from 'react';
import exchangeRateAdminService from '../../services/exchangeRateAdminService';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const formatRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

const defaultDateTo = () => new Date().toISOString().slice(0, 10);

const defaultDateFrom = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
};

const ExchangeRates = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 50 });
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [recalcRun, setRecalcRun] = useState(null);
  const [recalcStarting, setRecalcStarting] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadRecalcStatus = useCallback(async () => {
    try {
      const res = await exchangeRateAdminService.recalcLatest();
      if (res?.success) {
        setRecalcRun(res.data || null);
      }
    } catch {
      setRecalcRun(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await exchangeRateAdminService.list({
        date_from: dateFrom,
        date_to: dateTo,
        page,
        per_page: 50,
      });
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta(res.meta || { current_page: 1, last_page: 1, total: 0, per_page: 50 });
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo cargar tipos de cambio', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar tipos de cambio',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRecalcStatus();
  }, [loadRecalcStatus]);

  useEffect(() => {
    if (!recalcRun || (recalcRun.status === 'completed' && !recalcRun.completed_with_gap)) {
      return undefined;
    }
    const timer = setInterval(loadRecalcStatus, 4000);
    return () => clearInterval(timer);
  }, [recalcRun, loadRecalcStatus]);

  const handleRecalcStart = async () => {
    if (!window.confirm(
      '¿Actualizar TODOS los comprobantes con moneda dual (soles/dólares)? Puede tardar varios minutos. Debe estar activo el worker de colas (php artisan queue:work).'
    )) {
      return;
    }
    setRecalcStarting(true);
    try {
      const res = await exchangeRateAdminService.recalcStart();
      if (res.success) {
        setToast({ isOpen: true, message: res.message, type: 'success' });
        await loadRecalcStatus();
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo iniciar', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al iniciar regularización',
        type: 'error',
      });
    } finally {
      setRecalcStarting(false);
    }
  };

  const handleRecalcResume = async () => {
    setRecalcStarting(true);
    try {
      const res = await exchangeRateAdminService.recalcResume();
      if (res.success) {
        setToast({ isOpen: true, message: res.message, type: 'success' });
        setRecalcRun(res.data || null);
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo reanudar', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al reanudar',
        type: 'error',
      });
    } finally {
      setRecalcStarting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await exchangeRateAdminService.sync({ date_from: dateFrom, date_to: dateTo });
      if (res.success) {
        setToast({ isOpen: true, message: res.message || 'Tipos de cambio actualizados', type: 'success' });
        setPage(1);
        const listRes = await exchangeRateAdminService.list({
          date_from: dateFrom,
          date_to: dateTo,
          page: 1,
          per_page: 50,
        });
        if (listRes.success) {
          setRows(Array.isArray(listRes.data) ? listRes.data : []);
          setMeta(listRes.meta || meta);
        }
      } else {
        setToast({ isOpen: true, message: res.message || 'No se pudo sincronizar', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al sincronizar con Migo',
        type: 'error',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tipos de cambio (SUNAT)</h1>
        <p className="text-sm text-gray-600 mt-1">
          Tipos de cambio de compra y venta registrados en el sistema (fuente Migo). Se usan para moneda dual en
          comprobantes.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-2">Regularizar moneda dual en comprobantes</h2>
        <p className="text-xs text-gray-600 mb-3">
          Descarga tipos de cambio históricos y recalcula montos PEN/USD en documentos, guías, ventas y todas las líneas.
          Los comprobantes nuevos se actualizan al guardar; use esto para el histórico o tras cambiar tipos de cambio.
        </p>
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <button
            type="button"
            onClick={handleRecalcStart}
            disabled={recalcStarting || recalcRun?.status === 'running'}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {recalcStarting ? 'Encolando…' : 'Actualizar todos los comprobantes'}
          </button>
          {recalcRun
            && (recalcRun.completed_with_gap
              || (recalcRun.status !== 'completed' && recalcRun.status !== 'running')) && (
            <button
              type="button"
              onClick={handleRecalcResume}
              disabled={recalcStarting || recalcRun.status === 'running'}
              className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
            >
              Reanudar
            </button>
          )}
        </div>
        {recalcRun && (
          <div className="text-xs text-gray-700 space-y-1 border-t border-gray-100 pt-3">
            <p>
              <span className="font-medium">Estado:</span>{' '}
              {recalcRun.status === 'completed' ? 'Completado' : recalcRun.status === 'failed' ? 'Falló' : recalcRun.status === 'running' ? 'En curso' : recalcRun.status}
              {recalcRun.phase_label && recalcRun.phase !== 'completed'
                ? ` · ${recalcRun.phase_label}`
                : ''}
            </p>
            {recalcRun.percent != null && (
              <p>
                <span className="font-medium">Progreso:</span> {recalcRun.percent}% ({recalcRun.processed} / {recalcRun.total_estimate ?? '—'})
              </p>
            )}
            {recalcRun.completed_with_gap && (
              <p className="text-amber-800">
                Quedan comprobantes o líneas sin montos PEN/USD. Compruebe que el worker de colas esté activo (Supervisor:
                queue:work con colas default, exports y kardex) y pulse «Reanudar» o vuelva a «Actualizar todos los
                comprobantes».
              </p>
            )}
            {recalcRun.last_error && (
              <p className="text-red-600"><span className="font-medium">Error:</span> {recalcRun.last_error}</p>
            )}
            {recalcRun.status === 'running' && (
              <p className="text-amber-700">
                Si el porcentaje no avanza, revise el worker de colas (queue:work --queue=exports,kardex,default
                --memory=1024).
              </p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 disabled:opacity-50"
        >
          Buscar
        </button>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing || loading}
          className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {syncing ? 'Sincronizando…' : 'Sincronizar rango (Migo)'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-500">Cargando…</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-gray-500">
            No hay tipos de cambio en el rango. Use &quot;Sincronizar rango&quot; o amplíe las fechas.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Fecha</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Compra</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Venta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-900">
                    {formatDateLocaleLima(row.date)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-800">
                    {formatRate(row.purchase_rate)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-gray-800">
                    {formatRate(row.sale_rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {meta.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm">
            <span className="text-gray-600">
              Página {meta.current_page} de {meta.last_page} ({meta.total} registros)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={page >= meta.last_page || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ExchangeRates;
